import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Store, Users, Target, TrendingUp, Heart, Handshake, Download, Sparkles, Play, BadgeCheck, CreditCard } from "lucide-react";
import { Logo } from "../components/Logo";
import { LanguageToggle } from "../components/LanguageToggle";
import { Footer } from "../components/Footer";
import { AiAssistant } from "../components/AiAssistant";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const HERO_IMG =
  "https://images.pexels.com/photos/11161325/pexels-photo-11161325.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=600";

const EXAMPLE_VIDEOS = [
  { src: "https://videos.pexels.com/video-files/16624891/16624891-sd_360_640_30fps.mp4", poster: "https://images.pexels.com/videos/16624891/pexels-photo-16624891.jpeg?cs=tinysrgb&dpr=1&w=500", tag: "Le Bistro des Halles" },
  { src: "https://videos.pexels.com/video-files/6281229/6281229-sd_360_640_25fps.mp4", poster: "https://images.pexels.com/videos/6281229/pexels-photo-6281229.jpeg?cs=tinysrgb&dpr=1&w=500", tag: "La Piazza" },
  { src: "https://videos.pexels.com/video-files/20416781/20416781-sd_360_640_24fps.mp4", poster: "https://images.pexels.com/videos/20416781/pexels-photo-20416781.jpeg?cs=tinysrgb&dpr=1&w=500", tag: "Sweet Corner" },
  { src: "https://videos.pexels.com/video-files/8941864/8941864-sd_506_960_25fps.mp4", poster: "https://images.pexels.com/videos/8941864/pexels-photo-8941864.jpeg?cs=tinysrgb&dpr=1&w=500", tag: "Banh Mi House" },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600",
  "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600",
];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();

  const goApp = () => navigate(user ? (user.role === "admin" ? "/admin" : "/app") : "/login");

  const process = [
    { icon: Store, title: t("p1Title"), desc: t("p1Desc"), color: "bg-gold/15 text-gold" },
    { icon: Users, title: t("p2Title"), desc: t("p2Desc"), color: "bg-fuchsia-500/15 text-fuchsia-300" },
    { icon: Handshake, title: t("p3Title"), desc: t("p3Desc"), color: "bg-emerald-500/15 text-emerald-300" },
    { icon: Download, title: t("p4Title"), desc: t("p4Desc"), color: "bg-sky-500/15 text-sky-300" },
  ];

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <header className="sticky top-0 z-40 glass border-b border-cardline">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Logo size={30} />
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button
              onClick={() => navigate("/login")}
              data-testid="landing-login-btn"
              className="rounded-full border border-cardline bg-ink2/60 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold"
            >
              {t("login")}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative mx-auto max-w-5xl px-4 pt-8">
        <div className="grid items-center gap-6 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("heroTitle1")} <span className="text-gold">{t("heroTitle2")}</span> {t("heroTitle3")}
            </h1>
            <p className="mt-4 max-w-md text-base text-slate-300">{t("heroSubtitle")}</p>
            <div className="mt-7 flex max-w-sm flex-col gap-3">
              <button onClick={goApp} data-testid="hero-business-btn"
                className="group flex items-center justify-between rounded-2xl bg-ink2 px-5 py-4 text-left font-semibold text-white ring-1 ring-cardline transition-all hover:ring-gold">
                {t("imBusiness")}
                <ArrowRight className="text-gold transition-transform group-hover:translate-x-1" size={20} />
              </button>
              <button onClick={goApp} data-testid="hero-creator-btn"
                className="group flex items-center justify-between rounded-2xl border border-cardline bg-transparent px-5 py-4 text-left font-semibold text-white transition-all hover:border-gold">
                {t("imCreator")}
                <ArrowRight className="text-slate-400 transition-transform group-hover:translate-x-1" size={20} />
              </button>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex -space-x-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-8 w-8 rounded-full border-2 border-ink bg-gradient-to-br from-gold/70 to-ink3" />
                ))}
                <div className="grid h-8 w-8 place-items-center rounded-full border-2 border-ink bg-gold text-[10px] font-bold text-ink">2K</div>
              </div>
              <span className="text-sm text-slate-400">+2 000 {t("businessesSupported")}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-cardline">
              <img src={HERO_IMG} alt="creator" className="h-[360px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent" />
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs">
                <span className="font-semibold text-white">@emma.vibes</span>
                <span className="text-slate-300">128K</span>
                <Heart size={13} className="text-red-400" fill="currentColor" /> 3,2K
              </div>
            </div>
            <div className="absolute -bottom-4 right-3 w-40 rounded-2xl bg-white p-4 text-ink gold-glow">
              <TrendingUp className="text-gold" size={20} />
              <div className="mt-2 text-xs font-medium text-slate-500">{t("newClients")}</div>
              <div className="font-heading text-2xl font-extrabold text-emerald-600">+42%</div>
              <div className="text-[11px] text-slate-400">{t("onAverage")}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Concept + process */}
      <section className="mx-auto mt-24 max-w-5xl px-4">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold text-gold">
            <Sparkles size={13} /> {t("discoverConcept")}
          </span>
          <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl">{t("conceptTitle")}</h2>
          <p className="mx-auto mt-2 max-w-xl text-base text-slate-400">{t("conceptSub")}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {process.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`process-step-${i + 1}`}
              className="relative rounded-3xl border border-cardline bg-card2/50 p-5"
            >
              <div className="absolute right-4 top-4 font-heading text-4xl font-extrabold text-cardline">{i + 1}</div>
              <div className={`grid h-12 w-12 place-items-center rounded-2xl ${s.color}`}><s.icon size={24} /></div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-400">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Example videos */}
      <section className="mx-auto mt-24 max-w-5xl px-4">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t("exampleContent")}</h2>
            <p className="mt-1 text-sm text-slate-400">{t("exampleContentSub")}</p>
          </div>
          <Play className="text-gold" size={26} />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {EXAMPLE_VIDEOS.map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`example-video-${i}`}
              className="relative aspect-[3/4] overflow-hidden rounded-2xl border border-cardline bg-ink2"
            >
              <video src={v.src} poster={v.poster} autoPlay muted loop playsInline preload="metadata" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/95 to-transparent p-2.5">
                <div className="truncate text-[11px] font-semibold text-white">{v.tag}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Two audiences */}
      <section className="mx-auto mt-24 max-w-5xl px-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-3xl border border-gold/30 bg-gold/5 p-6">
            <div className="flex items-center gap-2 text-gold"><Store size={22} /><h3 className="font-heading text-xl font-bold">{t("forBusinessTitle")}</h3></div>
            <p className="mt-2 text-sm text-slate-300">{t("forBusinessDesc")}</p>
            <button onClick={goApp} data-testid="cta-business-card" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-ink">
              {t("imBusiness")} <ArrowRight size={16} />
            </button>
          </div>
          <div className="rounded-3xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-6">
            <div className="flex items-center gap-2 text-fuchsia-300"><Sparkles size={22} /><h3 className="font-heading text-xl font-bold">{t("forCreatorsTitle")}</h3></div>
            <p className="mt-2 text-sm text-slate-300">{t("forCreatorsDesc")}</p>
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink2/70 px-3 py-1 text-xs text-slate-300">
              <CreditCard size={13} className="text-fuchsia-300" /> {t("forCreatorsTitle")} · abonnement
            </div>
            <div>
              <button onClick={goApp} data-testid="cta-creator-card" className="mt-4 inline-flex items-center gap-2 rounded-full border border-fuchsia-400/50 px-5 py-2.5 text-sm font-bold text-fuchsia-200">
                {t("imCreator")} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="mx-auto mt-24 max-w-5xl px-4">
        <div className="grid items-center gap-8 rounded-3xl border border-cardline bg-card2/50 p-6 md:grid-cols-2 md:p-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 px-3 py-1 text-xs font-semibold text-sky-300"><Download size={14} /> {t("deliverablesTitle")}</div>
            <h2 className="mt-3 font-heading text-2xl font-bold sm:text-3xl">{t("deliverablesTitle")}</h2>
            <p className="mt-2 text-sm text-slate-400">{t("deliverablesDesc")}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-300">
              <span className="inline-flex items-center gap-1 rounded-full bg-ink2/70 px-3 py-1"><Download size={12} className="text-gold" /> {t("legendMission")}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-ink2/70 px-3 py-1"><BadgeCheck size={12} className="text-emerald-400" /> {t("verified")}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {GALLERY.map((g, i) => (
              <div key={i} className={`overflow-hidden rounded-2xl border border-cardline ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                <img src={g} alt="" className={`w-full object-cover ${i === 0 ? "h-full" : "h-24"}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto my-24 max-w-3xl px-4 text-center">
        <Sparkles className="mx-auto text-gold" size={28} />
        <h2 className="mt-3 font-heading text-3xl font-extrabold">{t("ctaTitle")}</h2>
        <button onClick={() => navigate("/login")} data-testid="landing-cta-btn"
          className="mx-auto mt-6 flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-bold text-ink transition-transform hover:scale-105 gold-glow">
          {t("ctaBtn")} <ArrowRight size={18} />
        </button>
      </section>

      <footer>
        <Footer />
      </footer>
      <AiAssistant />
    </div>
  );
}
